/**
 * streamFetch — pristine fetch helper for SSE / ReadableStream endpoints.
 *
 * Why this exists
 * ---------------
 * Hosted environments (CDNs, analytics SDKs, preview tooling) often
 * monkey-patch `window.fetch` to intercept, mirror or replay responses. On a
 * non-2xx response some of these wrappers call `response.text()` on the
 * ORIGINAL response object (not a clone), draining the body. The next call
 * to `await res.text()` or `res.body.getReader()` then throws
 * "Response.text: Body has already been consumed.".
 *
 * PostHog session-recording (network capture) does something similar when
 * body recording is on — see the `recordBody: false` flag in index.html.
 *
 * `public/index.html` stashes the pristine native fetch into
 * `window.__nativeFetch` BEFORE any third-party script can load. Prefer that.
 * Fall back to a persistent (attached, NOT detached — detaching can invalidate
 * the captured fetch in Firefox) hidden iframe's `contentWindow.fetch`. Last
 * resort: the patched `window.fetch`.
 *
 * Use this for any endpoint that streams (SSE, NDJSON, chunked, ReadableStream).
 * Regular CRUD calls should continue to use `axios` via `lib/api.js`.
 */

let _cachedRawFetch = null;

function _iframeFallback() {
    let iframe = document.getElementById("__cf_raw_fetch__");
    if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "__cf_raw_fetch__";
        iframe.setAttribute("aria-hidden", "true");
        iframe.tabIndex = -1;
        iframe.style.cssText =
            "display:none;width:0;height:0;border:0;position:absolute;";
        (document.body || document.documentElement).appendChild(iframe);
    }
    const cw = iframe.contentWindow;
    const f = cw && cw.fetch;
    return f ? (...args) => f.call(cw, ...args) : null;
}

/**
 * Returns a fetch implementation that bypasses any wrappers installed on
 * `window.fetch` by preview-environment scripts.
 */
export function getNativeFetch() {
    if (_cachedRawFetch) return _cachedRawFetch;
    if (typeof window === "undefined") {
        return (...args) => fetch(...args);
    }
    if (typeof window.__nativeFetch === "function") {
        _cachedRawFetch = window.__nativeFetch;
        return _cachedRawFetch;
    }
    try {
        const f = _iframeFallback();
        if (f) {
            _cachedRawFetch = f;
            return _cachedRawFetch;
        }
    } catch {
        /* fall through to patched fetch */
    }
    _cachedRawFetch = window.fetch.bind(window);
    return _cachedRawFetch;
}

/**
 * Convenience: same signature as `fetch(input, init)` but routed through the
 * pristine implementation.
 */
export function streamFetch(input, init) {
    return getNativeFetch()(input, init);
}

export default streamFetch;
